import { isAfter } from 'date-fns';

export const STORAGE_KEYS = {
  TOKEN: 'twgm-token',
  USER: 'twgm-user',
  APP_LOCALE: 'twgm-app-locale',
  IMPERSONATOR: 'twgm-impersonator',
  TEAM_TWRN: 'twgm-twrn',
  ORG: 'twgm-org',
  BANNER_IS_OPEN: 'twgm-banner-is-open',
  ORG_BUDGETS_BANNER_IS_OPEN: 'twgm-org-budgets-banner-is-open',
  ORG_BUDGETS_SUMMARY_COLLAPSED: 'twgm-org-budgets-summary-collapsed',
  AGREEMENTS_BANNER_IS_OPEN: 'twgm-agreements-banner-is-open',
  AGENT_DIRECTORY_BANNER_IS_OPEN: 'twgm-agent-directory-banner-is-open',
} as const;
export type StorageKey = (typeof STORAGE_KEYS)[keyof typeof STORAGE_KEYS];

interface LocalStorageItem<T> {
  value: T;
  expiresAt?: string;
}

export function setLocalStorageItem<T>(
  key: StorageKey,
  value: T,
  expiresAt?: Date,
  store: 'localStorage' | 'sessionStorage' = 'localStorage',
) {
  removeLocalStorageItem(key);
  const item: LocalStorageItem<T> = {
    value,
    expiresAt: expiresAt?.toISOString(),
  };
  if (store === 'localStorage') localStorage.setItem(key, JSON.stringify(item));
  else sessionStorage.setItem(key, JSON.stringify(item));
}

export function getLocalStorageItem<T>(key: StorageKey) {
  const serializedItem
    = localStorage.getItem(key) ?? sessionStorage.getItem(key);
  if (!serializedItem) return null;

  try {
    const item: LocalStorageItem<T> = JSON.parse(serializedItem);

    if (item.expiresAt && isAfter(new Date(), new Date(item.expiresAt))) {
      removeLocalStorageItem(key);
      return null;
    }

    return item.value;
  } catch (error) {
    // If JSON parsing fails (e.g., corrupted data), remove the item and return null
    console.warn(`[localStorage] Failed to parse item for key "${key}". Removing corrupted data.`, error);
    removeLocalStorageItem(key);
    return null;
  }
}

export function removeLocalStorageItem(key: StorageKey) {
  sessionStorage.removeItem(key);
  localStorage.removeItem(key);
}
